import { create } from 'zustand';
import {
  UploadApiError,
  abortUpload,
  completeUpload,
  confirmImage,
  createImage,
  deleteDraftImage,
  expectedPartBytes,
  fetchImage,
  fetchMyImages,
  fetchPartUrls,
  fetchParts,
  fetchUploadsConfig,
  initiateImageUpload,
  patchImage,
  patchImageStudy,
  putPart,
  touchUpload,
  type EnterpriseImage,
  type ImageStage,
  type ImageStudy,
} from '../../features/upload/api';
import { ENTERPRISE_UPLOADS_KEY, uploadStorageCleared } from '../../features/upload/storageKeys';
import { Semaphore, abortError, guardStall, isAbort, isTransient, sleep, withRetry } from '../../features/upload/transfer';
import { matchSidecar, mergeDrafts, parseSidecar, type MetadataDraft, type SidecarFormat } from '../../features/upload/sidecar';
import { parseDicomFile } from '../../features/upload/dicomParse';
import { followName, type NamedFile } from '../../features/upload/imageName';
import type { ImageKind, ImageType } from '../../data/imageTypes';
import { bytesOf, isSlide, isXray } from '../../features/upload/dicomSlides';
import { folderOf } from '../../features/upload/fileHandles';
import { LONE_MRXS, groupSlides, noSlidedatReason } from '../../features/upload/slides';
import { holdsOf, sameMembers, tarOf, type TarMember } from '../../features/upload/tar';
import { queryClient } from '../../hooks/queryClient';
import {
  adoptView,
  certainKindOf,
  createDetails,
  followType,
  deltaOf,
  draftFromView,
  fieldOfServerKey,
  imageBody,
  initialDraft,
  isEmptyWire,
  kindOf,
  shareStudy,
  studyBody,
  wireOf,
  type FileKind,
  type StudyGroup,
  type Wire,
} from './details';

/**
 * The enterprise upload page's state, over the server's DRAFTS
 * (server/src/http/drafts.ts, "enterprise: slide by slide").
 *
 * Every file dropped is an image row on the server before a byte is sent
 * (POST /images, in the workspace chosen in the header, with what the form
 * says), then its bytes go to the image — the same multipart transport, retry
 * and resume rules as the community uploader (community/upload/store.ts),
 * which this mirrors. Details are saved as they are typed, only what changed.
 *
 * The server converts an image as soon as its bytes are in and PUBLISHES it
 * once it is ingested AND its details are confirmed: by the person (Confirm,
 * POST /images/:id/ready) or by the LIS answering (the metadata worker). The
 * page polls while the server is working and shows where each image is.
 *
 * The rows outlive a reload (sessionStorage, minus the File objects): an
 * unfinished transfer waits for its file to be dropped again and resumes from
 * the parts that landed. The server's list of this person's recent uploads
 * (GET /images) fills in the rest, per workspace; a row the list does not
 * mention is asked about by id (GET /images/:id), and one the server no longer
 * has leaves (or, with its file still here, waits for a Retry to make it again).
 */

/** The transfer of a row's bytes. What happens after they are in is the server's `stage`. */
export type TransferPhase =
  | 'queued'
  | 'starting'
  | 'uploading'
  | 'finishing'
  | 'done'
  | 'failed'
  /** Cut by a reload (or never sent): the file has to be dropped again. */
  | 'waiting-file'
  /** An upload this person has running in another tab. */
  | 'elsewhere';

export type PrefillSource = SidecarFormat | 'dicom';

/** A save the server refused, and the form field it is about when it names one. */
export interface FieldError {
  field: keyof MetadataDraft | null;
  message: string;
}

export interface UploadRow {
  id: string;
  /** Sent with the image's create, so a retried create finds the image the first attempt made. */
  requestKey: string;
  /** The workspace (public id) the image goes to, pinned when the row was made; null is the account's default. */
  workspace: string | null;
  /** The image (`img_…`); null until the server has made it. */
  imageId: string | null;
  /** The bytes (the file, or a slide's tar: tar.ts); null for a row restored after a reload or listed from the server. */
  file: Blob | null;
  name: string;
  kind: FileKind;
  /** The Type the file's header is certain of — an X-ray is Radiography (dicomSlides isXray) — or absent: any its kind fits. */
  certainType?: ImageType | null;
  size: number;
  /** File.lastModified, so a file dropped again is the same bytes and not a namesake. 0 when unknown. */
  lastModified: number;
  /** A slide of several DICOM files, sent as one tar: its files, matched as a set when dropped again. `[]` for one listed as packed whose files this tab never saw. */
  members?: TarMember[];
  /** Bytes the server has, plus bytes in flight. */
  loaded: number;
  phase: TransferPhase;
  error: string | null;
  uploadId: string | null;
  partSize: number;
  partCount: number;
  /** partNumber → ETag of every part the server confirmed. A retry sends only the rest. */
  etags: Record<number, string>;
  /** The form. */
  draft: MetadataDraft;
  /** What a dropped file or the DICOM header said, for the form's "edited" marks; null when nothing was prefilled. */
  prefill: MetadataDraft | null;
  prefillSource: PrefillSource | null;
  /** The details as the server last took them (see details.ts); null before the image exists. */
  saved: Wire | null;
  /** When `saved` last changed (ms): a list asked for before then is older than it. */
  savedAt: number;
  /** The form says something the server has not taken yet. */
  dirty: boolean;
  detailsError: FieldError | null;
  /** Where the server has the image; null before it has said. */
  stage: ImageStage | null;
  /** 0 to 100 while the server converts or indexes it. */
  percent: number | null;
  /** The server's reason for a failed image. */
  stageError: string | null;
  /** Its details are confirmed (by a person, or resolved from the LIS). */
  ready: boolean;
  confirming: boolean;
  /** Why the last Confirm or Remove did not happen. */
  confirmError: string | null;
  study: ImageStudy | null;
  metadata: EnterpriseImage['metadata'] | null;
  /** Listed from the server rather than added in this tab. */
  remote: boolean;
  /** The server could say only the image's summary: blank fields in the form may hold values. */
  partial: boolean;
}

export const isMoving = (r: Pick<UploadRow, 'phase'>) => r.phase === 'queued' || r.phase === 'starting' || r.phase === 'uploading' || r.phase === 'finishing';
/** Nothing more to change here: shown to everyone, or failed. */
export const isLocked = (r: Pick<UploadRow, 'stage'>) => r.stage === 'published' || r.stage === 'failed';
/** Confirming is offered: the image is not published or failed, and its details are not confirmed yet. */
export const canConfirm = (r: Pick<UploadRow, 'stage' | 'ready' | 'phase'>) => !isLocked(r) && !r.ready && r.phase !== 'failed';
/** The stages that mean the image's bytes are on the server. */
const BYTES_IN: ReadonlySet<ImageStage> = new Set(['converting', 'ingesting', 'needs-details', 'published', 'failed']);

/** A row's own transfer percentage: an integer, 100 only once it is done. */
export function filePct(r: Pick<UploadRow, 'phase' | 'loaded' | 'size'>): number {
  switch (r.phase) {
    case 'done':
      return 100;
    case 'finishing':
      return 99;
    case 'uploading':
    case 'failed':
    case 'waiting-file':
      return r.size > 0 ? Math.min(99, Math.floor((Math.min(r.loaded, r.size) / r.size) * 100)) : 0;
    default:
      return 0;
  }
}

/** Tries per part; with the backoff cap that is a few minutes of a dead link. */
const PART_ATTEMPTS = 12;
/** Tries for the small control calls (create, initiate, part URLs, complete, details). */
const CALL_ATTEMPTS = 8;
const STALL_MS = 60_000;
const FINAL_MS = 5 * 60_000;
const URL_REFRESHES = 2;
const PROGRESS_EVERY_MS = 150;
const HEARTBEAT_FALLBACK_MS = 45_000;
const TAKEOVER_WAIT_CAP_MS = 5 * 60_000;
/** Typing settles for this long before the details go to the server. */
const SAVE_DEBOUNCE_MS = 600;
/** How often the page asks where its images are, while the server is working on them. */
export const POLL_MS = 3000;
/** How often to ask while nothing moves and a slide only waits for its metadata (the LIS may answer in hours). */
export const SLOW_POLL_MS = 30_000;
/** How far back the page lists this person's uploads. */
export const HISTORY_DAYS = 7;
const URL_WINDOW = 8;

const IMAGE_GONE = 'The server no longer has this image (an image whose file never arrives is removed after 24 hours). Retry uploads it again.';
export const UNIDENTIFIED = 'Add an accession number or a patient ID first: a slide is never shown without one.';
const FALLBACK_EXTS = ['svs', 'ndpi', 'tif', 'tiff', 'scn', 'dcm', 'jpg', 'jpeg', 'png', 'zip', 'pdf'];

/** One pool of in-flight parts for every file together (see the community store). */
const partGate = new Semaphore(4);
/** Each file's place in line for part permits: the order its transfer first started, so files finish in the order they were added. */
const rankOf = new Map<string, number>();
let nextRank = 0;
/** id → the controller that stops every request of that row, and its transfer. */
const running = new Map<string, { ctl: AbortController; done: Promise<TransferEnd> }>();
/** Images this tab removed: a list fetched before the delete landed must not bring them back. */
const withdrawn = new Set<string>();

interface TransferEnd {
  imageId: string | null;
  uploadId: string | null;
  /** The bytes are in (or the server said so). */
  landed: boolean;
}

/** The poll's timer (see the bottom of this file); here so reset() can stop it. */
const poll: { timer: ReturnType<typeof setTimeout> | undefined; busy: boolean; every: number } = { timer: undefined, busy: false, every: 0 };

/** @internal Test hook: whether a transfer is still going for this row. */
export function isTransferring(id: string): boolean {
  return running.has(id);
}

const uid = () => Math.random().toString(36).slice(2, 10);
function requestKeyNew(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID ? c.randomUUID() : `${uid()}${uid()}${uid()}`;
}

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i <= 0 ? '' : name.slice(i + 1).toLowerCase();
}

/** Why a file named .dcm is not a DICOM file (a web page saved as one), or null. The community uploader's rule. */
export function notDicomReasonFor(name: string, head: Uint8Array): string | null {
  if (!name.toLowerCase().endsWith('.dcm')) return null;
  const dicm = head.length >= 132 && String.fromCharCode(...head.subarray(128, 132)) === 'DICM';
  if (dicm) return null;
  const first = [...head].find((b) => b !== 0x20 && b !== 0x09 && b !== 0x0a && b !== 0x0d && b !== 0xef && b !== 0xbb && b !== 0xbf);
  if (first !== 0x3c) return null; // '<'
  return `${name} is not a DICOM file: it is a web page saved with a .dcm name. Download the file itself and upload that.`;
}

/** A dropped file's text. Blob.text is missing in some runtimes (jsdom); FileReader is everywhere. */
function readText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error('could not read the file'));
    r.readAsText(file);
  });
}

async function readHead(file: Blob, n: number): Promise<Uint8Array> {
  return new Uint8Array(await bytesOf(file.slice(0, n)));
}

class RefusedError extends Error {}

/** What the person reads when a transfer gives up. */
function describeFailure(err: unknown): string {
  if (err instanceof UploadApiError) {
    if (err.status === 401) return 'Your session ended. Sign in again, then retry the upload.';
    if (err.status === 403 && /workspace/.test(err.message)) return err.message.charAt(0).toUpperCase() + err.message.slice(1);
    if (err.status === 403) return 'The storage refused the upload (403). Check the deployment’s bucket permissions, then retry.';
    if (err.status === 404) return 'The server no longer has this upload. Retry sends the file again.';
    if (err.status === 0) return 'Upload interrupted. The connection dropped and did not come back. Retry continues where it left off.';
    return err.message;
  }
  if (isTransient(err)) return 'Upload interrupted. The connection dropped and did not come back. Retry continues where it left off.';
  return err instanceof Error ? err.message : String(err);
}

function partRetryable(err: unknown, refreshesLeft: () => number): boolean {
  if (!(err instanceof UploadApiError)) return false;
  if (err.status === 400) return true;
  if (err.status === 403) return refreshesLeft() > 0;
  return false;
}

/** What a refused details save says, and the form field it is about. */
export function detailsErrorOf(err: unknown, part: 'image' | 'study'): FieldError {
  if (err instanceof UploadApiError) {
    const invalid = /^invalid ([\w.]+)$/.exec(err.message);
    if (err.status === 400 && invalid) {
      return { field: fieldOfServerKey(invalid[1], part), message: `The server did not accept this ${invalid[1].replace(/[_.]/g, ' ')}.` };
    }
    if (err.status === 409) return { field: part === 'study' ? 'accession' : null, message: err.message.charAt(0).toUpperCase() + err.message.slice(1) + '.' };
    if (err.status === 404) return { field: null, message: 'The server no longer has this image. It may have been removed.' };
    if (err.status === 401) return { field: null, message: 'Your session ended. Sign in again; the details are kept here.' };
    if (err.status === 0 || isTransient(err)) return { field: null, message: 'Could not reach the server. The details are kept here and sent with the next change.' };
    return { field: null, message: err.message };
  }
  if (isTransient(err)) return { field: null, message: 'Could not reach the server. The details are kept here and sent with the next change.' };
  return { field: null, message: err instanceof Error ? err.message : String(err) };
}

/** Whether the form says something `saved` does not have. */
const hasDelta = (draft: MetadataDraft, saved: Wire | null) => !isEmptyWire(deltaOf(wireOf(draft).wire, saved));

/** Every study group, as nothing: what a study made bare has. */
const STUDY_NOTHING: Wire['study'] = Object.fromEntries(
  (['study_id', 'description', 'sex', 'age', 'referring_physician', 'study_datetime', 'accession', 'patient'] satisfies StudyGroup[]).map((g) => [g, null]),
);

/** What a list entry says about where the image is. */
const stageOf = (v: EnterpriseImage): Pick<UploadRow, 'stage' | 'percent' | 'stageError' | 'ready' | 'metadata' | 'study'> => ({
  stage: v.stage,
  percent: v.percent ?? null,
  stageError: v.error,
  ready: v.ready === true,
  metadata: v.metadata ?? null,
  study: v.study ?? null,
});

/** The file a row's image is named for (imageName.ts): a slide of several files is named for the slide. */
const namedFile = (r: Pick<UploadRow, 'name' | 'members'>): NamedFile => ({ name: r.name, slide: !!r.members });

function newRow(name: string, over: Partial<UploadRow>): UploadRow {
  const kind = kindOf(name);
  return {
    id: uid(), requestKey: requestKeyNew(), workspace: null, imageId: null, file: null, name, kind, size: 0, lastModified: 0, loaded: 0,
    phase: 'queued', error: null, uploadId: null, partSize: 0, partCount: 0, etags: {},
    draft: initialDraft(kind), prefill: null, prefillSource: null, saved: null, savedAt: 0, dirty: false, detailsError: null,
    stage: null, percent: null, stageError: null, ready: false, confirming: false, confirmError: null, study: null, metadata: null, remote: false, partial: false,
    ...over,
  };
}

/** A row for an image this tab did not make: from the list of this person's uploads. */
function listedRow(v: EnterpriseImage, workspace: string | null): UploadRow {
  const name = v.name ?? v.image_id;
  const bytesIn = BYTES_IN.has(v.stage);
  const { draft, saved, partial } = draftFromView(v);
  const resumable = !bytesIn && !!v.upload;
  const phase: TransferPhase = bytesIn ? 'done' : resumable || v.stage === 'waiting-file' ? 'waiting-file' : 'elsewhere';
  return newRow(name, {
    // A slide packed by the page is named for the slide, not for a file: a scanned image, of several files.
    ...(v.packed ? { kind: 'wsi' as const, members: [] } : {}),
    workspace, imageId: v.image_id, size: v.size ?? 0, loaded: bytesIn ? v.size ?? 0 : 0, phase,
    uploadId: resumable ? v.upload!.id : null, partSize: v.upload?.partSize ?? 0, partCount: v.upload?.partCount ?? 0,
    draft, saved, savedAt: Date.now(), remote: true, partial, ...stageOf(v),
  });
}

// ── persistence ─────────────────────────────────────────────────────────────

type SavedRow = Omit<UploadRow, 'file'>;

/** The rows as they come back after a reload: a transfer that was not done waits for its file. */
export function restoreRows(saved: unknown): UploadRow[] {
  const rows = (saved as { rows?: unknown } | null)?.rows;
  if (!Array.isArray(rows)) return [];
  return (rows as Partial<SavedRow>[])
    .filter((r): r is SavedRow => !!r && typeof r.id === 'string' && typeof r.name === 'string' && 'imageId' in r && !!r.draft && typeof r.requestKey === 'string')
    .map((r) => ({
      ...r,
      file: null,
      confirming: false,
      partial: r.partial ?? false,
      ...(r.phase === 'done' || r.phase === 'elsewhere' ? {} : { phase: 'waiting-file' as const, error: null }),
    }));
}

function loadRows(): UploadRow[] {
  try {
    const raw = window.sessionStorage.getItem(ENTERPRISE_UPLOADS_KEY);
    return raw ? restoreRows(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

function saveRows(rows: UploadRow[]): void {
  if (uploadStorageCleared()) return;
  try {
    if (!rows.length) window.sessionStorage.removeItem(ENTERPRISE_UPLOADS_KEY);
    else window.sessionStorage.setItem(ENTERPRISE_UPLOADS_KEY, JSON.stringify({ rows: rows.map(({ file: _file, ...rest }) => rest) }));
  } catch {
    /* storage unavailable: the rows live until the tab reloads */
  }
}

// ── the store ───────────────────────────────────────────────────────────────

interface State {
  rows: UploadRow[];
  selectedId: string | null;
  /** The header's workspace, pushed in by the page; where new rows go and whose history is listed. */
  targetWorkspace: string | null;
  /** One-line notices from the last drop (unsupported types, unpaired metadata files). */
  notices: string[];
  acceptedExts: string[] | null;
  setTargetWorkspace: (publicId: string | null) => void;
  ensureConfig: () => Promise<void>;
  /** Images become rows and start uploading; .json files (and a .dcm's own tags) prefill the form of their image. */
  addFiles: (files: File[]) => Promise<void>;
  select: (id: string | null) => void;
  /** The form changed: saved after typing settles. */
  setFields: (id: string, patch: Partial<MetadataDraft>) => void;
  /** Save every unsaved detail now (or one row's). */
  flushDetails: (id?: string) => Promise<void>;
  /** The person confirms the row's details: published once it is ingested. */
  confirm: (id: string) => Promise<void>;
  /** Stop the transfer and withdraw the image from the server. Not for a published one. */
  remove: (id: string) => Promise<void>;
  retry: (id: string) => void;
  /** This person's recent uploads to the header's workspace, merged into the rows. */
  loadHistory: () => Promise<void>;
  /** @internal The poll's word on rows its list did not mention: asked about by id. */
  checkMissing: (listed: ReadonlyArray<EnterpriseImage>, candidates: ReadonlyArray<UploadRow>) => Promise<void>;
  /** Square the rows with the server's list: stages, a complete that landed before a reload. `adopt` adds the images the rows do not have. */
  sync: (images: ReadonlyArray<EnterpriseImage>, opts: { workspace: string | null; askedAt: number; adopt?: boolean }) => void;
  clearNotices: () => void;
  clearMessage: (id: string) => void;
  /** @internal Stop every transfer and forget every row (tests, sign-out). */
  reset: () => void;
}

export const useEnterpriseUpload = create<State>((set, get, api) => {
  const patchRow = (id: string, p: Partial<UploadRow>) => set((s) => ({ rows: s.rows.map((r) => (r.id === id ? { ...r, ...p } : r)) }));
  const rowNow = (id: string) => get().rows.find((r) => r.id === id) ?? null;
  const published = (before: ImageStage | null, after: ImageStage | null) => {
    if (after === 'published' && before !== 'published') void queryClient.invalidateQueries({ queryKey: ['studies'] });
  };

  /**
   * A view the server answered for a row: where it is, and the details it now
   * has that the form had nothing to say about. A Specimen ID it brings names
   * the image while its Name is still a default (followName), and that is saved.
   */
  const takeView = (id: string, imageId: string, v: EnterpriseImage, saved?: Wire) => {
    const now = rowNow(id);
    if (!now || now.imageId !== imageId) return;
    let draft = now.draft;
    let nextSaved = saved ?? now.saved;
    if (nextSaved) {
      const adopted = adoptView(draft, nextSaved, v);
      if (adopted) ({ draft, saved: nextSaved } = adopted);
    }
    const named = followName(now.draft, draft, namedFile(now));
    patchRow(id, { ...stageOf(v), draft: named, saved: nextSaved, ...(saved ? { savedAt: Date.now() } : {}), dirty: hasDelta(named, nextSaved) });
    if (named !== draft) scheduleSave(id);
    published(now.stage, v.stage);
  };

  /**
   * Slides of one case share its study: what one row just saved to it is what
   * the others' forms say too, where they have nothing of their own to say. A
   * stale sibling would otherwise send the old patient back with its next edit.
   */
  const shareCase = (fromId: string, caseId: string, groups: Wire['study']) =>
    set((s) => ({
      rows: s.rows.map((b) => {
        if (b.id === fromId || b.study?.public_id !== caseId || !b.saved || isLocked(b) || saving.has(b.id)) return b;
        const shared = shareStudy(b.draft, b.saved, groups);
        return shared ? { ...b, ...shared, savedAt: Date.now(), dirty: hasDelta(shared.draft, shared.saved) } : b;
      }),
    }));

  /**
   * The server no longer has a row's image (withdrawn in another tab, expired
   * unsent, deleted from its study). A row whose file is still here waits for
   * Retry to make the image again with what the form says; any other leaves,
   * with a notice.
   */
  const gone = (id: string, imageId: string) => {
    const r = rowNow(id);
    if (!r || r.imageId !== imageId) return;
    clearSave(id);
    if (r.file && !running.has(id)) {
      patchRow(id, {
        phase: 'failed', error: IMAGE_GONE, imageId: null, requestKey: requestKeyNew(), uploadId: null, partSize: 0, partCount: 0, etags: {}, loaded: 0,
        saved: null, dirty: true, detailsError: null, confirmError: null, stage: null, percent: null, stageError: null, ready: false, metadata: null, study: null,
      });
      return;
    }
    set((s) => ({
      rows: s.rows.filter((x) => x.id !== id),
      selectedId: s.selectedId === id ? null : s.selectedId,
      notices: [...s.notices, `${r.name} is no longer on the server${r.phase === 'done' ? '' : ' (an image whose file never arrives is removed after 24 hours)'}.`],
    }));
  };

  /** Rows being asked about by id, so two lists do not ask twice. */
  const checking = new Set<string>();
  /**
   * The rows a list did not mention, asked about one by one: the list covers
   * this person's images of one workspace over a few days, so a row missing
   * from it may be older, or filed where the default workspace put it — or gone.
   */
  const checkMissing = async (listed: ReadonlyArray<EnterpriseImage>, candidates: ReadonlyArray<UploadRow>) => {
    const known = new Set(listed.map((v) => v.image_id));
    const missing = candidates.filter((r) => r.imageId && !known.has(r.imageId) && !running.has(r.id) && !checking.has(r.id));
    await Promise.all(
      missing.map(async (r) => {
        const imageId = r.imageId!;
        checking.add(r.id);
        const askedAt = Date.now();
        try {
          const v = await fetchImage(imageId);
          const now = rowNow(r.id);
          if (!now || now.imageId !== imageId || running.has(r.id)) return;
          if (v) get().sync([v], { workspace: now.workspace, askedAt });
          else gone(r.id, imageId);
        } catch {
          /* unreachable: asked again with the next list */
        } finally {
          checking.delete(r.id);
        }
      }),
    );
  };

  // ── details ───────────────────────────────────────────────────────────────
  // Saved as they are typed, per image, one save at a time: a slower earlier
  // answer must not overwrite a later one's. The study goes first, so a new
  // accession has moved the image before its own details are written.

  const saveTimers = new Map<string, ReturnType<typeof setTimeout>>();
  const saving = new Map<string, Promise<void>>();
  const clearSave = (id: string) => {
    clearTimeout(saveTimers.get(id));
    saveTimers.delete(id);
  };
  const saveNow = (id: string): Promise<void> => {
    clearSave(id);
    const next = (saving.get(id) ?? Promise.resolve()).then(async () => {
      const r = rowNow(id);
      if (!r || !r.imageId || !r.dirty) return;
      const imageId = r.imageId;
      const delta = deltaOf(wireOf(r.draft).wire, r.saved);
      if (isEmptyWire(delta)) {
        patchRow(id, { dirty: false, detailsError: null });
        return;
      }
      let saved: Wire = r.saved ?? { image: {}, study: {} };
      let part: 'image' | 'study' = 'study';
      try {
        if (Object.keys(delta.study).length) {
          const v = await withRetry(() => patchImageStudy(imageId, studyBody(delta.study)), { attempts: CALL_ATTEMPTS });
          saved = { ...saved, study: { ...saved.study, ...delta.study } };
          takeView(id, imageId, v, saved);
          saved = rowNow(id)?.saved ?? saved;
          if (v.study) shareCase(id, v.study.public_id, delta.study);
        }
        part = 'image';
        if (Object.keys(delta.image).length) {
          const v = await withRetry(() => patchImage(imageId, imageBody(delta.image)), { attempts: CALL_ATTEMPTS });
          saved = { ...saved, image: { ...saved.image, ...delta.image } };
          takeView(id, imageId, v, saved);
        }
        if (rowNow(id)?.imageId === imageId) patchRow(id, { detailsError: null });
      } catch (err) {
        if (err instanceof UploadApiError && err.status === 404) gone(id, imageId);
        // Kept here and still dirty: the next change (or Confirm) sends them again.
        else if (rowNow(id)?.imageId === imageId) patchRow(id, { detailsError: detailsErrorOf(err, part) });
      }
    });
    saving.set(id, next);
    void next.finally(() => {
      if (saving.get(id) === next) saving.delete(id);
    });
    return next;
  };
  const scheduleSave = (id: string) => {
    clearSave(id);
    saveTimers.set(id, setTimeout(() => void saveNow(id), SAVE_DEBOUNCE_MS));
  };

  /** The row's image id once its transfer has made it (null if the transfer ends without one). */
  const imageIdOf = (id: string): Promise<string | null> => {
    const now = rowNow(id);
    const t = running.get(id);
    if (!now || now.imageId || !t) return Promise.resolve(now?.imageId ?? null);
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        unsub();
        resolve(rowNow(id)?.imageId ?? null);
      };
      const unsub = api.subscribe(() => {
        const r = rowNow(id);
        if (!r || r.imageId) finish();
      });
      void t.done.then(finish, finish);
    });
  };

  // ── the transfer ──────────────────────────────────────────────────────────

  const transfer = async (id: string, ctl: AbortController): Promise<TransferEnd> => {
    const rank = rankOf.get(id) ?? 0;
    const r = rowNow(id);
    if (!r) return { imageId: null, uploadId: null, landed: false };
    const file = r.file;
    if (!file) return { imageId: r.imageId, uploadId: r.uploadId, landed: false };
    const signal = ctl.signal;
    const retryCall = <T,>(fn: (sig?: AbortSignal) => Promise<T>) => withRetry((_a, sig) => fn(sig), { attempts: CALL_ATTEMPTS, signal });

    let imageId = r.imageId;
    let imageGone = false;
    let uploadId = r.uploadId;
    let partSize = r.partSize;
    let partCount = r.partCount;
    let heartbeatMs = HEARTBEAT_FALLBACK_MS;
    let landed = false;
    let resumed = !!(uploadId && partCount > 0);
    const etagByPart = new Map<number, string>(resumed ? Object.entries(r.etags).map(([n, e]) => [Number(n), e]) : []);
    const urlByPart = new Map<number, string>();
    const inFlight = new Map<number, number>();
    const snapshot = () => Object.fromEntries(etagByPart);
    const confirmedBytes = () => [...etagByPart.keys()].reduce((a, n) => a + expectedPartBytes(r.size, partSize, n, partCount), 0);
    let doneBytes = 0;
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    let flushedEtags = -1;
    const flush = () => {
      flushTimer = undefined;
      if (signal.aborted) return;
      let loaded = doneBytes;
      for (const v of inFlight.values()) loaded += v;
      const p: Partial<UploadRow> = { loaded: Math.min(r.size, loaded) };
      if (etagByPart.size !== flushedEtags) {
        flushedEtags = etagByPart.size;
        p.etags = snapshot();
      }
      patchRow(id, p);
    };
    const progress = (now = false) => {
      if (now) {
        if (flushTimer) clearTimeout(flushTimer);
        flush();
      } else if (!flushTimer) flushTimer = setTimeout(flush, PROGRESS_EVERY_MS);
    };

    /**
     * The image, made with what the form says now. A detail the server refuses
     * must not keep the slide from uploading: the image is then made bare, and
     * the save that follows names the field.
     */
    const ensureImage = async () => {
      if (imageId) return;
      const now = rowNow(id) ?? r;
      const { wire } = wireOf(now.draft);
      const { image, study } = createDetails(wire);
      // A slide's files go as one tar, and the image is named for the slide (drafts.ts `packed`, `holds`). Its kind is said only when the name is certain of it (a scanner's format), so the server can check the Type fits; otherwise the converter settles it.
      const certain = certainKindOf(r);
      const base = { name: r.name, ...(certain ? { kind: certain } : {}), request_key: now.requestKey, ...(r.members ? { packed: 'tar' as const, holds: holdsOf(r.members) } : {}), ...(now.workspace ? { workspace: now.workspace } : {}) };
      let v: EnterpriseImage;
      let saved: Wire = wire;
      try {
        v = await retryCall((sig) => createImage({ ...base, ...image, study }, sig));
      } catch (err) {
        const detail = err instanceof UploadApiError && err.status === 400 && /^invalid (?!file name|request_key)/.test(err.message);
        if (!detail) throw err;
        v = await retryCall((sig) => createImage(base, sig));
        saved = { image: {}, study: { ...STUDY_NOTHING } };
      }
      imageId = v.image_id;
      if (signal.aborted) throw abortError(); // Remove deletes it with the id this resolves
      // A row listed from the server for this same image (a reload cut the create's answer) is this row.
      set((s) => ({ rows: s.rows.filter((x) => x.id === id || x.imageId !== v.image_id) }));
      patchRow(id, { imageId });
      takeView(id, v.image_id, v, saved);
      if (rowNow(id)?.dirty) void saveNow(id);
    };

    const startFresh = async () => {
      etagByPart.clear();
      urlByPart.clear();
      doneBytes = 0;
      resumed = false;
      patchRow(id, { phase: 'starting', error: null, loaded: 0, etags: {}, uploadId: null });
      // A slide's files were read to group them (dicomSlides.ts) already.
      if (!r.members && r.name.toLowerCase().endsWith('.dcm')) {
        const refused = notDicomReasonFor(r.name, await readHead(file, 132));
        if (refused) throw new RefusedError(refused);
      }
      await ensureImage();
      let init: Awaited<ReturnType<typeof initiateImageUpload>>;
      let waited = 0;
      for (;;) {
        try {
          init = await retryCall((sig) => initiateImageUpload(imageId!, { size: r.size, contentType: file.type || undefined }, sig));
          break;
        } catch (err) {
          if (err instanceof UploadApiError && err.status === 404) imageGone = true;
          if (err instanceof UploadApiError && err.status === 409 && err.reason === 'has-file') {
            landed = true;
            return;
          }
          const own = err instanceof UploadApiError && err.status === 409 && err.reason === 'uploading' && err.body?.mine === true;
          const wait = own ? Math.min(60_000, Math.max(1000, Number(err.body?.retryAfterMs) || 5000)) : 0;
          if (!own || waited + wait > TAKEOVER_WAIT_CAP_MS) throw err;
          waited += wait;
          await sleep(wait, signal);
        }
      }
      uploadId = init.uploadId;
      partSize = init.partSize;
      partCount = init.partCount;
      heartbeatMs = init.heartbeatMs ?? HEARTBEAT_FALLBACK_MS;
      partGate.setCapacity(init.partConcurrency);
      init.urls.forEach((u) => urlByPart.set(u.partNumber, u.url));
      if (signal.aborted) {
        void abortUpload(uploadId);
        throw abortError();
      }
      patchRow(id, { uploadId, partSize, partCount });
    };

    let urlFetch: Promise<void> | null = null;
    const ensureUrl = async (n: number) => {
      for (let tries = 0; !urlByPart.has(n); tries++) {
        if (tries > 2) throw new UploadApiError(`no URL for part ${n}`, 0);
        if (!urlFetch) {
          const wanted: number[] = [];
          for (let k = n; k <= partCount && wanted.length < URL_WINDOW; k++) if (!urlByPart.has(k) && !etagByPart.has(k)) wanted.push(k);
          if (!wanted.includes(n)) wanted.unshift(n);
          urlFetch = retryCall((sig) => fetchPartUrls(uploadId!, wanted, sig))
            .then((urls) => urls.forEach((u) => urlByPart.set(u.partNumber, u.url)))
            .finally(() => (urlFetch = null));
        }
        await urlFetch;
      }
      return urlByPart.get(n)!;
    };

    const uploadPart = (n: number, round: AbortSignal) => {
      const blob = file.slice((n - 1) * partSize, Math.min(n * partSize, r.size));
      let refreshes = 0;
      return withRetry(
        async () => {
          // The permit first, then the URL: a file waits its turn behind the
          // files added before it (Semaphore), perhaps for longer than a URL lives.
          const release = await partGate.acquire(round, rank);
          try {
            const url = await ensureUrl(n);
            const { etag } = await guardStall(
              (sig, progressed) =>
                putPart(url, blob, {
                  signal: sig,
                  onProgress: (l) => {
                    inFlight.set(n, l);
                    progressed(l >= blob.size);
                    progress();
                  },
                }),
              { stallMs: STALL_MS, finalMs: FINAL_MS },
              round,
            );
            etagByPart.set(n, etag);
            urlByPart.delete(n);
            inFlight.delete(n);
            doneBytes += blob.size;
            progress(true);
          } catch (err) {
            inFlight.delete(n);
            if (err instanceof UploadApiError && err.status === 403) {
              urlByPart.delete(n);
              refreshes++;
            }
            throw err;
          } finally {
            release();
          }
        },
        { attempts: PART_ATTEMPTS, signal: round, retryable: (err) => isTransient(err) || partRetryable(err, () => URL_REFRESHES - refreshes + 1) },
      );
    };

    const sendMissing = async () => {
      const queue: number[] = [];
      for (let n = 1; n <= partCount; n++) if (!etagByPart.has(n)) queue.push(n);
      if (!queue.length) return;
      patchRow(id, { phase: 'uploading' });
      const roundCtl = new AbortController();
      const onRowAbort = () => roundCtl.abort();
      signal.addEventListener('abort', onRowAbort, { once: true });
      try {
        const workers = Array.from({ length: Math.min(partGate.capacity, queue.length) }, async () => {
          while (queue.length && !roundCtl.signal.aborted) {
            const n = queue.shift()!;
            await uploadPart(n, roundCtl.signal);
          }
        });
        try {
          await Promise.all(workers);
        } catch (err) {
          roundCtl.abort();
          await Promise.allSettled(workers);
          inFlight.clear();
          throw err;
        }
      } finally {
        signal.removeEventListener('abort', onRowAbort);
      }
    };

    let heartbeat: ReturnType<typeof setInterval> | undefined;
    try {
      if (resumed && imageId) {
        patchRow(id, { phase: 'starting', error: null });
        const held = await retryCall((sig) => fetchParts(uploadId!, sig));
        if (!held) {
          await startFresh();
        } else if (held.completed) {
          landed = true;
        } else {
          etagByPart.clear();
          for (const p of held.parts) etagByPart.set(p.partNumber, p.etag);
          partSize = held.partSize;
          partCount = held.partCount;
          patchRow(id, { partSize, partCount, etags: snapshot() });
          doneBytes = confirmedBytes();
        }
      } else {
        await startFresh();
      }
      if (!landed) heartbeat = setInterval(() => { if (uploadId) void touchUpload(uploadId, signal).catch(() => undefined); }, heartbeatMs);
      const maxRounds = Math.ceil(partCount / 20) + 2;
      for (let round = 0; !landed; round++) {
        try {
          await sendMissing();
          patchRow(id, { phase: 'finishing', loaded: r.size });
          await retryCall((sig) => completeUpload(uploadId!, [...etagByPart].map(([partNumber, etag]) => ({ partNumber, etag })), sig));
          landed = true;
        } catch (err) {
          if (resumed && err instanceof UploadApiError && err.status === 404) {
            await startFresh();
            continue;
          }
          const missing = err instanceof UploadApiError && err.status === 400 ? err.missing ?? [] : [];
          if (!missing.length || round >= maxRounds) throw err;
          missing.forEach((n) => etagByPart.delete(n));
          doneBytes = confirmedBytes();
          patchRow(id, { etags: snapshot() });
        }
      }
      if (signal.aborted) throw abortError();
      progress(true);
      const stage = rowNow(id)?.stage;
      // The server queued the conversion with the complete; the poll takes it from here.
      const queued = !stage || stage === 'waiting-file' || stage === 'uploading';
      patchRow(id, { phase: 'done', loaded: r.size, error: null, etags: snapshot(), ...(queued ? { stage: 'converting' as const, percent: 0 } : {}) });
    } catch (err) {
      if (flushTimer) clearTimeout(flushTimer);
      if (isAbort(err) || signal.aborted) return { imageId, uploadId, landed };
      ctl.abort();
      const lost = err instanceof UploadApiError && err.status === 404;
      patchRow(id, {
        phase: 'failed',
        error: imageGone ? IMAGE_GONE : describeFailure(err),
        etags: lost ? {} : snapshot(),
        ...(lost ? { uploadId: null, loaded: 0 } : { loaded: confirmedBytes() }),
        // Retry makes the image again, with what the form says.
        ...(imageGone ? { imageId: null, requestKey: requestKeyNew(), saved: null, stage: null, percent: null, stageError: null, ready: false, metadata: null, study: null, dirty: false, detailsError: null } : {}),
      });
    } finally {
      if (heartbeat) clearInterval(heartbeat);
    }
    return { imageId: imageGone ? null : imageId, uploadId, landed };
  };

  const run = (id: string) => {
    if (running.has(id)) return;
    if (!rankOf.has(id)) rankOf.set(id, nextRank++);
    const ctl = new AbortController();
    const entry = { ctl, done: Promise.resolve<TransferEnd>({ imageId: null, uploadId: null, landed: false }) };
    running.set(id, entry);
    entry.done = transfer(id, ctl).finally(() => {
      if (running.get(id) === entry) running.delete(id);
    });
  };

  /**
   * Prefill for a new file: its .dcm tags, under a paired .json (the file wins
   * field by field), over the defaults and its Name (initialDraft). A Specimen
   * ID they say names the image, unless they say a series description too; a
   * modality they say sets the Type (followType).
   */
  const prefillFor = (kind: FileKind, file: NamedFile, certain: ImageKind | null, sources: Array<{ draft: MetadataDraft; source: PrefillSource }>, certainType: ImageType | null = null) => {
    const start = initialDraft(kind, file, certainType);
    if (!sources.length) return { draft: start, prefill: null, prefillSource: null };
    let draft = start;
    for (const s of sources) draft = mergeDrafts(s.draft, draft);
    draft = followType(start, followName(start, draft, file), certain, certainType);
    return { draft, prefill: { ...draft }, prefillSource: sources[sources.length - 1].source };
  };

  return {
    rows: loadRows(),
    selectedId: null,
    targetWorkspace: null,
    notices: [],
    acceptedExts: null,

    setTargetWorkspace: (publicId) => set({ targetWorkspace: publicId }),

    ensureConfig: async () => {
      if (get().acceptedExts) return;
      try {
        const cfg = await fetchUploadsConfig();
        set({ acceptedExts: cfg.extensions });
      } catch {
        // Unreachable: the known registry, so the page still renders. The create re-validates.
        set({ acceptedExts: FALLBACK_EXTS });
      }
    },

    addFiles: async (files) => {
      await get().ensureConfig();
      const accepted = get().acceptedExts ?? FALLBACK_EXTS;
      const workspace = get().targetWorkspace;
      const notices: string[] = [];
      const jsons: Array<{ name: string; draft: MetadataDraft; format: SidecarFormat }> = [];
      const others: File[] = [];
      for (const f of files) {
        if (extOf(f.name) === 'json') {
          try {
            const parsed = parseSidecar(JSON.parse(await readText(f)));
            if (parsed) jsons.push({ name: f.name, ...parsed });
            else notices.push(`"${f.name}" skipped: not a JSON object`);
          } catch {
            notices.push(`"${f.name}" skipped: malformed JSON`);
          }
        } else others.push(f);
      }
      // A slide of several files is one image, packed as one tar (slides.ts,
      // tar.ts): a MIRAX with its data folder, the .dcm levels of one series.
      // Its files are taken with it whatever their names; a file on its own
      // must be of an accepted type.
      const { picked: grouped, loneMirax, noSlidedat } = await groupSlides(others, { dcm: accepted.includes('dcm'), mrxs: accepted.includes('mrxs') });
      for (const f of loneMirax) notices.push(`"${f.name}" skipped: ${LONE_MRXS}`);
      for (const f of noSlidedat) notices.push(`"${f.name}" skipped: ${noSlidedatReason(1)}`);
      const unsupported: File[] = [];
      const picked = grouped.filter((p) => {
        if (isSlide(p) || accepted.includes(extOf(p.name))) return true;
        unsupported.push(p as File);
        return false;
      });
      // A folder of files no page takes (a MIRAX data folder dropped without
      // its .mrxs) is said once, not once per file.
      const byFolder = new Map<string | null, File[]>();
      for (const f of unsupported) byFolder.set(folderOf(f), [...(byFolder.get(folderOf(f)) ?? []), f]);
      for (const [folder, group] of byFolder) {
        if (folder && group.length > 1) notices.push(`"${folder}/" skipped: ${group.length} files of unsupported types`);
        else group.forEach((f) => notices.push(`"${f.name}" skipped: unsupported type`));
      }

      const visible = (x: UploadRow) => x.workspace === null || x.workspace === workspace;
      const fresh: UploadRow[] = [];
      const resumed: string[] = [];
      const surfaced: string[] = [];
      const consumed = new Set<string>();
      const jsonNames = jsons.map((j) => j.name);
      for (const p of picked) {
        const rows = get().rows;
        const slide = isSlide(p) ? p : null;
        const tar = slide ? tarOf(slide.files, slide.paths) : null;
        const name = p.name;
        const bytes: Pick<UploadRow, 'file' | 'size' | 'lastModified'> & { members?: TarMember[] } = tar && slide
          ? { file: tar.blob, size: tar.size, lastModified: Math.max(0, ...slide.files.map((x) => x.lastModified)), members: tar.members }
          : { file: p as File, size: (p as File).size, lastModified: (p as File).lastModified };
        // Dropped again after a reload (or an earlier visit's unsent image): the same bytes resume. A slide's files are matched as a set.
        const waiting = rows.find((x) => visible(x) && x.phase === 'waiting-file' && !resumed.includes(x.id) && x.name === name
          && (tar ? x.members !== undefined && (!x.members.length || sameMembers(x.members, tar.members))
            : !x.members && (x.size === 0 || x.size === bytes.size) && (x.lastModified === 0 || x.lastModified === bytes.lastModified)));
        // A metadata file named for the slide, or for any of its files.
        const match = matchSidecar(name, jsonNames) ?? slide?.files.map((x) => matchSidecar(x.name, jsonNames)).find((m) => m !== undefined);
        const json = match ? jsons.find((j) => j.name === match) : undefined;
        if (waiting) {
          if (json) consumed.add(json.name);
          resumed.push(waiting.id);
          const patch: Partial<UploadRow> = { ...bytes, error: null, phase: 'queued' };
          if (json && !isLocked(waiting)) {
            patch.draft = followType(waiting.draft, followName(waiting.draft, mergeDrafts(json.draft, waiting.draft), namedFile(waiting)), certainKindOf(waiting), waiting.certainType ?? null);
            patch.prefill = { ...patch.draft };
            patch.prefillSource = json.format;
            patch.dirty = hasDelta(patch.draft, waiting.saved);
          }
          patchRow(waiting.id, patch);
          continue;
        }
        const same = rows.find((x) => !x.remote && x.file && visible(x) && x.phase !== 'failed' && x.name === name && x.size === bytes.size && x.lastModified === bytes.lastModified);
        if (same || fresh.some((x) => x.name === name && x.size === bytes.size && x.lastModified === bytes.lastModified)) {
          if (same) surfaced.push(same.id);
          notices.push(`"${name}" is already in the list`);
          continue;
        }
        if (json) consumed.add(json.name);
        const kind = slide ? 'wsi' : kindOf(name);
        const sources: Array<{ draft: MetadataDraft; source: PrefillSource }> = [];
        // What the file's own tags say: a DICOM slide's from its base level, the largest of its files.
        const tagged = slide ? (slide.holds === 'dcm' ? [...slide.files].sort((a, b) => b.size - a.size)[0] : null) : extOf(name) === 'dcm' ? (p as File) : null;
        if (tagged) {
          const tags = await parseDicomFile(tagged);
          if (tags) sources.push({ draft: tags, source: 'dicom' });
        }
        if (json) sources.push({ draft: json.draft, source: json.format });
        // An X-ray's header settles its Type: Radiography, whatever the file's own modality says.
        const certainType: ImageType | null = !slide && isXray(p as File) ? 'radiography' : null;
        fresh.push(newRow(name, { ...bytes, kind, workspace, certainType, ...prefillFor(kind, { name, slide: !!slide }, certainKindOf({ name, members: bytes.members }), sources, certainType) }));
      }

      // A metadata file dropped on its own fills the form of the image it pairs with.
      const adopted: string[] = [];
      for (const j of jsons) {
        if (consumed.has(j.name)) continue;
        const target = get().rows.find((x) => visible(x) && matchSidecar(x.name, [j.name]) !== undefined);
        if (!target) {
          notices.push(`"${j.name}" matches no image in the list: drop it together with its image`);
          continue;
        }
        if (isLocked(target)) {
          notices.push(`"${j.name}": ${target.name} is ${target.stage === 'published' ? 'imported' : 'failed'}; its details are not changed here`);
          continue;
        }
        adopted.push(target.id);
        const draft = followType(target.draft, followName(target.draft, mergeDrafts(j.draft, target.draft), namedFile(target)), certainKindOf(target), target.certainType ?? null);
        patchRow(target.id, { draft, prefill: { ...draft }, prefillSource: j.format, dirty: hasDelta(draft, target.saved) });
        scheduleSave(target.id);
      }

      set((s) => {
        const touched = [...adopted, ...resumed, ...surfaced];
        return {
          rows: [...fresh, ...s.rows.filter((x) => touched.includes(x.id)), ...s.rows.filter((x) => !touched.includes(x.id))],
          notices,
          selectedId: fresh[0]?.id ?? touched[0] ?? s.selectedId,
        };
      });
      [...fresh.map((x) => x.id), ...resumed].forEach(run);
      resumed.forEach((id) => {
        if (rowNow(id)?.dirty) scheduleSave(id);
      });
    },

    select: (id) => set({ selectedId: id }),

    setFields: (id, p) => {
      const r = rowNow(id);
      if (!r || isLocked(r)) return;
      // A Specimen ID typed names the image while its Name is still a default (imageName.ts).
      const draft = followName(r.draft, { ...r.draft, ...p }, namedFile(r));
      patchRow(id, { draft, dirty: hasDelta(draft, r.saved), confirmError: null });
      scheduleSave(id);
    },

    flushDetails: async (id) => {
      const ids = id ? [id] : get().rows.filter((r) => r.dirty || saving.has(r.id)).map((r) => r.id);
      await Promise.all(ids.map((x) => saveNow(x)));
    },

    confirm: async (id) => {
      const r = rowNow(id);
      if (!r || r.confirming || !canConfirm(r)) return;
      patchRow(id, { confirming: true, confirmError: null });
      let imageId: string | null = null;
      try {
        imageId = r.imageId ?? (await imageIdOf(id));
        if (!imageId) {
          patchRow(id, { confirmError: 'This image is not on the server yet: its upload has to start first.' });
          return;
        }
        await saveNow(id);
        const now = rowNow(id);
        if (!now || now.imageId !== imageId) return;
        const problem = wireOf(now.draft).problems[0];
        if (problem) {
          patchRow(id, { confirmError: problem.message });
          return;
        }
        if (now.dirty) {
          patchRow(id, { confirmError: now.detailsError ? `The details are not saved: ${now.detailsError.message}` : 'The details are not saved yet.' });
          return;
        }
        const confirmed = imageId;
        const v = await withRetry(() => confirmImage(confirmed), { attempts: CALL_ATTEMPTS });
        takeView(id, confirmed, v);
      } catch (err) {
        if (err instanceof UploadApiError && err.status === 404 && imageId) gone(id, imageId);
        else patchRow(id, { confirmError: err instanceof UploadApiError && err.status === 400 && err.reason === 'unidentified' ? UNIDENTIFIED : describeFailure(err) });
      } finally {
        patchRow(id, { confirming: false });
      }
    },

    remove: async (id) => {
      const r = rowNow(id);
      if (!r || r.stage === 'published') return;
      set((s) => ({ rows: s.rows.filter((x) => x.id !== id), selectedId: s.selectedId === id ? null : s.selectedId }));
      clearSave(id);
      // The transfer stops first and settles: its image, or its upload, may have been made during the stop.
      const t = running.get(id);
      t?.ctl.abort();
      const end = t ? await t.done.catch(() => null) : null;
      const imageId = end?.imageId ?? r.imageId;
      const uploadId = end?.uploadId ?? r.uploadId;
      const landed = end?.landed ?? (r.phase === 'done' || r.phase === 'elsewhere');
      if (uploadId && !landed) await abortUpload(uploadId);
      if (!imageId) return;
      withdrawn.add(imageId);
      try {
        await withRetry(() => deleteDraftImage(imageId), { attempts: CALL_ATTEMPTS });
      } catch (err) {
        // Refused (being indexed, or published meanwhile) or unreachable: still there, so the row comes back and says why.
        withdrawn.delete(imageId);
        const message = err instanceof Error ? err.message : String(err);
        set((s) =>
          s.rows.some((x) => x.imageId === imageId)
            ? {}
            : { rows: [{ ...r, imageId, file: null, phase: r.phase === 'done' ? 'done' : 'failed', confirming: false, confirmError: `Could not be removed: ${message}` }, ...s.rows] },
        );
      }
    },

    retry: (id) => {
      const r = rowNow(id);
      if (!r || !r.file || running.has(id) || r.phase !== 'failed') return;
      patchRow(id, { error: null, phase: 'queued' });
      run(id);
    },

    loadHistory: async () => {
      const workspace = get().targetWorkspace;
      const askedAt = Date.now();
      try {
        const images = await fetchMyImages(workspace ?? undefined, HISTORY_DAYS);
        if (get().targetWorkspace !== workspace) return; // switched while asking: that answer is for another list
        get().sync(images, { workspace, askedAt, adopt: true });
        await checkMissing(images, get().rows.filter((r) => r.workspace === workspace || r.workspace === null));
      } catch {
        /* the page still works with the rows it has */
      }
    },

    sync: (images, { workspace, askedAt, adopt }) => {
      const byId = new Map(images.map((v) => [v.image_id, v]));
      const s = get();
      let changed = false;
      /** Rows whose Name followed a Specimen ID the server now has: theirs to save. */
      const named: string[] = [];
      const rows = s.rows.map((r) => {
        const v = r.imageId ? byId.get(r.imageId) : undefined;
        if (!v) return r;
        const next: UploadRow = { ...r, ...stageOf(v) };
        const moving = running.has(r.id);
        if (BYTES_IN.has(v.stage) && r.phase !== 'done' && !moving) Object.assign(next, { phase: 'done', loaded: r.size || v.size || 0, error: null });
        // Another tab's upload that stopped: this person's to finish here, once the file is dropped again.
        if (r.phase === 'elsewhere' && !BYTES_IN.has(v.stage) && (v.upload || v.stage === 'waiting-file')) Object.assign(next, { phase: 'waiting-file' });
        if (!next.uploadId && v.upload && next.phase === 'waiting-file') Object.assign(next, { uploadId: v.upload.id, partSize: v.upload.partSize, partCount: v.upload.partCount, etags: {} });
        // What the server filled in, where the form had nothing of its own to say; never over a save newer than the list.
        if (r.saved && !moving && !saving.has(r.id) && askedAt >= r.savedAt) {
          const taken = adoptView(next.draft, r.saved, v);
          if (taken) {
            const draft = followName(r.draft, taken.draft, namedFile(r));
            if (draft !== taken.draft) named.push(r.id);
            Object.assign(next, { draft, saved: taken.saved, dirty: hasDelta(draft, taken.saved) });
          }
        }
        const differs = (Object.keys(next) as (keyof UploadRow)[]).some((k) => JSON.stringify(next[k]) !== JSON.stringify(r[k]));
        if (!differs) return r;
        changed = true;
        published(r.stage, v.stage);
        return next;
      });
      let added: UploadRow[] = [];
      if (adopt) {
        const known = new Set(rows.map((r) => r.imageId));
        added = images.filter((v) => !known.has(v.image_id) && !withdrawn.has(v.image_id)).map((v) => listedRow(v, workspace));
      }
      if (!changed && !added.length) return;
      set({ rows: [...rows, ...added] });
      named.forEach(scheduleSave);
    },

    checkMissing,

    clearNotices: () => set({ notices: [] }),
    clearMessage: (id) => patchRow(id, { confirmError: null }),

    reset: () => {
      for (const [id, t] of running) {
        clearSave(id);
        t.ctl.abort();
      }
      saveTimers.forEach((t) => clearTimeout(t));
      saveTimers.clear();
      clearTimeout(poll.timer);
      poll.timer = undefined;
      withdrawn.clear();
      set({ rows: [], selectedId: null, notices: [] });
    },
  };
});

/** Whether a row is actively moving on the server (converting, indexing, publishing), so the page asks often. */
function movesFast(r: UploadRow): boolean {
  return needsPolling(r) && (r.phase === 'elsewhere' || r.stage === 'converting' || r.stage === 'ingesting' || (r.ready && r.metadata === null));
}

/** Whether the server is still working on a row, so the page should ask again. */
function needsPolling(r: UploadRow): boolean {
  if (!r.imageId || isLocked(r)) return false;
  if (r.phase === 'elsewhere') return true;
  if (r.phase !== 'done') return false;
  return r.stage === 'converting' || r.stage === 'ingesting' || r.metadata !== null || r.ready;
}

// ── the poll ────────────────────────────────────────────────────────────────
// One timer, asking for the lists of the workspaces whose rows the server is
// still working on, until none is.
{
  const schedule = () => {
    const pending = useEnterpriseUpload.getState().rows.filter(needsPolling);
    if (!pending.length) {
      clearTimeout(poll.timer);
      poll.timer = undefined;
      return;
    }
    const every = pending.some(movesFast) ? POLL_MS : SLOW_POLL_MS;
    if (poll.busy) return;
    // A slow wait already set is cut short when something starts moving.
    if (poll.timer && poll.every <= every) return;
    clearTimeout(poll.timer);
    poll.every = every;
    poll.timer = setTimeout(async () => {
      poll.timer = undefined;
      const workspaces = [...new Set(useEnterpriseUpload.getState().rows.filter(needsPolling).map((r) => r.workspace))];
      poll.busy = true;
      try {
        await Promise.all(
          workspaces.map(async (ws) => {
            const askedAt = Date.now();
            try {
              const listed = await fetchMyImages(ws ?? undefined, HISTORY_DAYS);
              const st = useEnterpriseUpload.getState();
              st.sync(listed, { workspace: ws, askedAt });
              await st.checkMissing(listed, st.rows.filter((r) => r.workspace === ws && needsPolling(r)));
            } catch {
              /* the next tick asks again */
            }
          }),
        );
      } finally {
        poll.busy = false;
      }
      schedule();
    }, every);
  };
  useEnterpriseUpload.subscribe((st, prev) => {
    if (st.rows !== prev.rows) schedule();
  });
  schedule();
}

// The rows are kept across a refresh: written at most once a second while they
// change, and on the way out of the page.
{
  let timer: ReturnType<typeof setTimeout> | undefined;
  const saveNow = () => {
    if (timer) clearTimeout(timer);
    timer = undefined;
    saveRows(useEnterpriseUpload.getState().rows);
  };
  useEnterpriseUpload.subscribe((s, prev) => {
    if (s.rows === prev.rows) return;
    if (!timer) timer = setTimeout(saveNow, 1000);
  });
  if (typeof window !== 'undefined') window.addEventListener('pagehide', saveNow);
}
// Details a reload cut before they were saved go now.
void useEnterpriseUpload.getState().flushDetails();
